import { BIZLY_OS_ROLES } from 'constants/roles';
import { StaffMemberType } from 'hooks/queries/BizlyOS/useStaffQuery';

const findStaffMember = (
    staffMembers: StaffMemberType[],
    userId: string | number | undefined,
    userEmail?: string
): StaffMemberType | undefined => {
    if (userId != null && userId !== '') {
        const normalizedUserId = String(userId);
        const byId = staffMembers.find(member => String(member.id) === normalizedUserId);
        if (byId) {
            return byId;
        }
    }

    if (userEmail) {
        const normalizedEmail = userEmail.trim().toLowerCase();
        return staffMembers.find(member => member.email.trim().toLowerCase() === normalizedEmail);
    }

    return undefined;
};

/**
 * Checks if a user has admin privileges
 * @param userId The user ID to check
 * @param staffMembers Array of staff members
 * @param platformRole Current user's platform role
 * @param userEmail Logged-in user email — fallback when staff id does not match user id
 * @returns boolean indicating if the user has admin privileges
 */
export const checkAdminPrivileges = (
    userId: string | number | undefined,
    staffMembers: StaffMemberType[] | undefined,
    platformRole?: string,
    userEmail?: string
): boolean => {
    // Check if the user is a platform admin
    if (platformRole === BIZLY_OS_ROLES.ADMIN) {
        return true;
    }

    // Check if the user is a venue staff admin
    if (staffMembers?.length) {
        const currentStaffMember = findStaffMember(staffMembers, userId, userEmail);
        return currentStaffMember?.admin ?? false;
    }

    return false;
};

/**
 * Bizly's own staff, as opposed to a customer's team admin. The API sets platformRole to
 * 'administrator' for @bizly.com addresses and internal roles; `isTeamAdmin` is a different
 * question and never grants this.
 */
export const isBizlyAdmin = (user?: Pick<Bizly.User, 'platformRole'>): boolean =>
    user?.platformRole === BIZLY_OS_ROLES.ADMIN;
